import { ValueObject } from '../../../shared/models/ValueObject';

/**
 * 사용자 역할 값 객체
 */
export class UserRole extends ValueObject<string> {
  private readonly _value: string;
  private readonly _permissions: Set<string>;

  // 역할 상수 (나중에 초기화)
  static readonly ADMIN: UserRole;
  static readonly OWNER: UserRole;
  static readonly STUDENT: UserRole;
  static readonly PARENT: UserRole;

  constructor(value: string, permissions: Set<string> = new Set()) {
    super();
    this.validate(value);
    this._value = value;
    this._permissions = permissions;
  }

  get value(): string {
    return this._value;
  }

  get permissions(): ReadonlySet<string> {
    return this._permissions;
  }

  private validate(value: string): void {
    if (!value) {
      throw new Error('역할은 비어있을 수 없습니다');
    }

    const validRoles = ['admin', 'owner', 'student', 'parent'];
    if (!validRoles.includes(value)) {
      throw new Error(`유효하지 않은 역할입니다: ${value}`);
    }
  }

  /**
   * 권한 확인
   */
  hasPermission(permission: string): boolean {
    // 와일드카드 권한 확인
    const permissions = Array.from(this._permissions);
    for (const perm of permissions) {
      if (perm.endsWith('*')) {
        const prefix = perm.slice(0, -1);
        if (permission.startsWith(prefix)) {
          return true;
        }
      } else if (perm === permission) {
        return true;
      }
    }
    return false;
  }

  /**
   * 관리자 권한 확인
   */
  isAdmin(): boolean {
    return this._value === 'admin';
  }

  /**
   * 점주 권한 확인
   */
  isOwner(): boolean {
    return this._value === 'owner' || this.isAdmin();
  }

  /**
   * 학생 권한 확인
   */
  isStudent(): boolean {
    return this._value === 'student';
  }

  protected equalsCore(vo: UserRole): boolean {
    return this._value === vo._value;
  }

  protected copy(): UserRole {
    return new UserRole(this._value, new Set(this._permissions));
  }

  toString(): string {
    return this._value;
  }

  /**
   * 팩토리 메서드
   */
  static create(value: string): UserRole {
    switch (value) {
      case 'admin':
        return UserRole.ADMIN;
      case 'owner':
        return UserRole.OWNER;
      case 'student':
        return UserRole.STUDENT;
      case 'parent':
        return UserRole.PARENT;
      default:
        throw new Error(`알 수 없는 역할: ${value}`);
    }
  }
}

// 정적 필드 초기화
(UserRole as any).ADMIN = new UserRole('admin', new Set([
  'admin.*',
  'owner.*',
  'user.*',
  'seat.*',
  'payment.*',
  'notification.*'
]));

(UserRole as any).OWNER = new UserRole('owner', new Set([
  'owner.*',
  'seat.manage',
  'user.manage',
  'payment.view',
  'notification.send'
]));

(UserRole as any).STUDENT = new UserRole('student', new Set([
  'seat.reserve',
  'seat.checkin',
  'payment.make',
  'user.profile.view',
  'user.profile.edit'
]));

(UserRole as any).PARENT = new UserRole('parent', new Set([
  'payment.make',
  'user.child.view',
  'notification.receive'
]));